/**
 * Self-service transfer error copy (#611, @dylan219: "add clarity into the error
 * messaging on the webapp to show what error they received").
 *
 * The "Transfer to automated self-service" action (Internet Password SCRN-22 /
 * CCF-17 and the registration self-serve transfers) posts to
 * `/api/genesys/transfer-to-self-serve`. Its BFF handler returns a DISTINCT
 * reason for every failure branch, but the SPA previously collapsed all of them
 * (except `not_online` and the Genesys-502 case) into one generic
 * "Could not transfer to self-service. Please try again." — so an agent (or
 * support) could not tell WHICH condition fired. This maps each BFF reason to
 * actionable copy PLUS a short `(ref: <reason>)` tag so the exact error is
 * visible and reportable, per the #80/#82/#94 inline-error convention. All
 * messages are non-blocking — the button stays so the agent can retry.
 *
 * BFF contract (server/src/api.ts `makeGenesysTransferHandler`):
 *   400 { error: 'missing_genesys_token' }
 *   409 { error: 'not_online' }
 *   403 { error: 'customer_mismatch' | 'no_server_identity' | 'open24_mismatch' | 'conversation_not_authorized' }
 *   502 { error: 'genesys_unavailable', code: '<genesys status>' }
 *   503 { error: 'integration_not_configured' }  (unset/mis-set self-service DID)
 */

/** The shape of the error the generated `openapi-fetch` client surfaces. */
export interface TransferApiError {
  error?: string;
  code?: string;
  /**
   * #731 — server-side diagnostic carried on the failure body (the missing
   * config env var on the fail-closed 503, or Genesys's own machine code +
   * message when it refuses the blind transfer). Surfaced to the console log
   * per @dylan219 ("make sure these new logs will show in the network and
   * console logs") — the body is already in the HAR/network capture.
   */
  detail?: string;
}

/**
 * #731 — mirror a failed transfer to the browser CONSOLE so a tester's
 * console-log capture (not just CloudWatch) carries the diagnostic:
 * `status` + the full failure body, including the BFF's `detail`. The body is
 * echoed verbatim because it is already the tester-facing network-capture
 * payload — it carries only config names + integration machine codes, never
 * secrets or customer data.
 */
export function logSelfServeTransferFailure(
  status: number,
  apiError: TransferApiError | undefined,
): void {
  console.error('[genesys-transfer] transfer failed', {
    status,
    ...apiError,
  });
}

const GENERIC = 'Could not transfer to self-service. Please try again.';

/** `not_online` is a normal, self-explanatory state — its own plain copy. */
const NOT_ONLINE_COPY = 'Transfer is only available on a live call.';

/** Human-readable, actionable copy keyed by the BFF `error` reason. */
const REASON_COPY: Record<string, string> = {
  missing_genesys_token:
    'No live-call session was found for this transfer. Reconnect to the Genesys call and try again.',
  customer_mismatch:
    'The verified customer does not match the live caller. Re-verify the customer on this call and try again.',
  // #611 — DISTINCT from customer_mismatch: there is no identity bound to this
  // live call to transfer with (the call carries no IVR Open24 number and no
  // customer has been ID&V'd on this call). Surfacing this as "does not match"
  // was the misleading copy @dylan219/philipc39 flagged.
  no_server_identity:
    'This call has no Open24 number and no customer has been identified on it, so the transfer could not be performed. Complete ID&V on this call (or ensure the call carries an Open24 number) and try again.',
  open24_mismatch:
    "The caller's Open24 number does not match the verified customer. Re-check ID&V and try again.",
  conversation_not_authorized:
    'You are not connected as a participant on this live call, so the transfer could not be performed.',
  integration_not_configured:
    'Self-service transfer is not configured for this environment. Ask your administrator to set the self-service transfer number.',
};

/**
 * Map a failed transfer response to a specific, agent-facing message with a
 * `(ref: …)` tag identifying the exact error. `not_online` keeps its plain copy
 * (it is an expected, self-explanatory state, not an error to report).
 */
export function selfServeTransferErrorMessage(
  status: number,
  apiError: TransferApiError | undefined,
): string {
  const reason = apiError?.error;

  // `not_online` is a normal state, not a reportable error — no ref tag.
  if (reason === 'not_online') return NOT_ONLINE_COPY;

  if (status === 502 || reason === 'genesys_unavailable') {
    const code = apiError?.code;
    return `Could not transfer to self-service${
      code ? ` (Genesys error ${code})` : ''
    }. Please try again. (ref: genesys_unavailable)`;
  }

  if (reason && REASON_COPY[reason]) {
    return `${REASON_COPY[reason]} (ref: ${reason})`;
  }

  // Unknown reason — still surface whatever identifier we have so it is
  // reportable, rather than a bare generic message.
  const ref = reason || (status ? `http_${status}` : undefined);
  return ref ? `${GENERIC} (ref: ${ref})` : GENERIC;
}
